import { API_BASE_URL } from './path';

interface QueryRequestType {
  path: string;
  params?: Record<string, string>;
}

interface MutationRequestType {
  path: string;
  params?: Record<string, string>;
  body?: Record<string, unknown> | FormData;
  headers?: Record<string, string>;
}

// Public fetch without credentials for unauthenticated endpoints
export const fetchPublicData = async <T>({
  path,
  params,
}: QueryRequestType): Promise<T> => {
  try {
    const queryString = params
      ? `?${new URLSearchParams(params).toString()}`
      : '';
    const url = `${API_BASE_URL}${path}${queryString}`;

    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      } as HeadersInit,
    });

    if (!response.ok) {
      const errorText = await response.text();

      if (response.status === 0) {
        throw new Error(
          'CORS Error: The server is not allowing requests from this origin.',
        );
      }

      if (response.status === 500) {
        try {
          const errorJson = JSON.parse(errorText) as {
            detail?: string;
            message?: string;
          };
          throw new Error(
            `Server Error: ${errorJson.detail ?? errorJson.message ?? errorText}`,
          );
        } catch {
          throw new Error(`Server Error (500): ${errorText}`);
        }
      }

      throw new Error(`API error: ${String(response.status)} - ${errorText}`);
    }

    const data = (await response.json()) as T;
    return data;
  } catch (_) {
    if (_ instanceof TypeError && _.message === 'Failed to fetch') {
      throw new Error(
        `Network Error: Could not connect to the server. Please check if it's running.`,
      );
    }

    throw _;
  }
};

export const fetchData = async <T>({
  path,
  params,
}: QueryRequestType): Promise<T> => {
  try {
    const queryString = params
      ? `?${new URLSearchParams(params).toString()}`
      : '';
    const url = `${API_BASE_URL}${path}${queryString}`;

    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Origin: window.location.origin,
        'x-api-key':
          (import.meta.env.VITE_WA_API_KEY as string | undefined) ?? '',
      } as HeadersInit,
    });

    if (!response.ok) {
      const errorText = await response.text();

      if (response.status === 0) {
        throw new Error(
          'CORS Error: The server is not allowing requests from this origin.',
        );
      }

      if (response.status === 500) {
        try {
          const errorJson = JSON.parse(errorText) as {
            detail?: string;
            message?: string;
          };
          throw new Error(
            `Server Error: ${errorJson.detail ?? errorJson.message ?? errorText}`,
          );
        } catch {
          throw new Error(`Server Error (500): ${errorText}`);
        }
      }

      throw new Error(`API error: ${String(response.status)} - ${errorText}`);
    }

    const data = (await response.json()) as T;
    return data;
  } catch (_) {
    if (_ instanceof TypeError && _.message === 'Failed to fetch') {
      throw new Error(
        `Network Error: Could not connect to the server. Please check if it's running.`,
      );
    }

    throw _;
  }
};

export const postData = async <T>({
  path,
  params,
  body,
  headers = {},
}: MutationRequestType): Promise<T> => {
  try {
    const queryString = params
      ? `?${new URLSearchParams(params).toString()}`
      : '';
    const url = `${API_BASE_URL}${path}${queryString}`;

    const isFormData = body instanceof FormData;
    const contentTypeHeader = isFormData
      ? {}
      : { 'Content-Type': 'application/json' };

    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        Origin: window.location.origin,
        'x-api-key':
          (import.meta.env.VITE_WA_API_KEY as string | undefined) ?? '',
        ...contentTypeHeader,
        ...headers,
      } as HeadersInit,
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();

      if (response.status === 0) {
        throw new Error(
          'CORS Error: The server is not allowing requests from this origin.',
        );
      }

      if (response.status === 500) {
        try {
          const errorJson = JSON.parse(errorText) as {
            detail?: string;
            message?: string;
          };
          throw new Error(
            `Server Error: ${errorJson.detail ?? errorJson.message ?? errorText}`,
          );
        } catch {
          throw new Error(`Server Error (500): ${errorText}`);
        }
      }

      throw new Error(`API error: ${String(response.status)} - ${errorText}`);
    }

    const data = (await response.json()) as T;
    return data;
  } catch (_) {
    if (_ instanceof TypeError && _.message === 'Failed to fetch') {
      throw new Error(
        `Network Error: Could not connect to the server. Please check if it's running.`,
      );
    }

    throw _;
  }
};

export const putData = async <T>({
  path,
  params,
  body,
  headers = {},
}: MutationRequestType): Promise<T> => {
  try {
    const queryString = params
      ? `?${new URLSearchParams(params).toString()}`
      : '';
    const url = `${API_BASE_URL}${path}${queryString}`;

    const isFormData = body instanceof FormData;
    const contentTypeHeader = isFormData
      ? {}
      : { 'Content-Type': 'application/json' };

    const response = await fetch(url, {
      method: 'PUT',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        Origin: window.location.origin,
        'x-api-key':
          (import.meta.env.VITE_WA_API_KEY as string | undefined) ?? '',
        ...contentTypeHeader,
        ...headers,
      } as HeadersInit,
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();

      if (response.status === 0) {
        throw new Error(
          'CORS Error: The server is not allowing requests from this origin.',
        );
      }

      if (response.status === 500) {
        try {
          const errorJson = JSON.parse(errorText) as {
            detail?: string;
            message?: string;
          };
          throw new Error(
            `Server Error: ${errorJson.detail ?? errorJson.message ?? errorText}`,
          );
        } catch {
          throw new Error(`Server Error (500): ${errorText}`);
        }
      }

      throw new Error(`API error: ${String(response.status)} - ${errorText}`);
    }

    const data = (await response.json()) as T;
    return data;
  } catch (_) {
    if (_ instanceof TypeError && _.message === 'Failed to fetch') {
      throw new Error(
        `Network Error: Could not connect to the server. Please check if it's running.`,
      );
    }

    throw _;
  }
};

export const deleteData = async <T>({
  path,
  params,
  headers = {},
}: MutationRequestType): Promise<T> => {
  try {
    const queryString = params
      ? `?${new URLSearchParams(params).toString()}`
      : '';
    const url = `${API_BASE_URL}${path}${queryString}`;

    const response = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        Origin: window.location.origin,
        'Content-Type': 'application/json',
        'x-api-key':
          (import.meta.env.VITE_WA_API_KEY as string | undefined) ?? '',
        ...headers,
      } as HeadersInit,
    });

    if (!response.ok) {
      const errorText = await response.text();

      if (response.status === 0) {
        throw new Error(
          'CORS Error: The server is not allowing requests from this origin.',
        );
      }

      if (response.status === 500) {
        try {
          const errorJson = JSON.parse(errorText) as {
            detail?: string;
            message?: string;
          };
          throw new Error(
            `Server Error: ${errorJson.detail ?? errorJson.message ?? errorText}`,
          );
        } catch {
          throw new Error(`Server Error (500): ${errorText}`);
        }
      }

      throw new Error(`API error: ${String(response.status)} - ${errorText}`);
    }

    const data = (await response.json()) as T;
    return data;
  } catch (_) {
    if (_ instanceof TypeError && _.message === 'Failed to fetch') {
      throw new Error(
        `Network Error: Could not connect to the server. Please check if it's running.`,
      );
    }

    throw _;
  }
};

export const patchData = async <T>({
  path,
  params,
  body,
  headers = {},
}: MutationRequestType): Promise<T> => {
  try {
    const queryString = params
      ? `?${new URLSearchParams(params).toString()}`
      : '';
    const url = `${API_BASE_URL}${path}${queryString}`;

    const isFormData = body instanceof FormData;
    const contentTypeHeader = isFormData
      ? {}
      : { 'Content-Type': 'application/json' };

    const response = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        Origin: window.location.origin,
        'x-api-key':
          (import.meta.env.VITE_WA_API_KEY as string | undefined) ?? '',
        ...contentTypeHeader,
        ...headers,
      } as HeadersInit,
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();

      if (response.status === 0) {
        throw new Error(
          'CORS Error: The server is not allowing requests from this origin.',
        );
      }

      if (response.status === 500) {
        try {
          const errorJson = JSON.parse(errorText) as {
            detail?: string;
            message?: string;
          };
          throw new Error(
            `Server Error: ${errorJson.detail ?? errorJson.message ?? errorText}`,
          );
        } catch {
          throw new Error(`Server Error (500): ${errorText}`);
        }
      }

      throw new Error(`API error: ${String(response.status)} - ${errorText}`);
    }

    const data = (await response.json()) as T;
    return data;
  } catch (_) {
    if (_ instanceof TypeError && _.message === 'Failed to fetch') {
      throw new Error(
        `Network Error: Could not connect to the server. Please check if it's running.`,
      );
    }

    throw _;
  }
};
